import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resetPassword } from "../api/auth";
import { validatePassword } from "../utils/validation";
import logoSmall from "../assets/logo_orange.svg";

export function ResetPassword() {
  const { t } = useTranslation();
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get("token"), [location.search]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({ password: false, confirm: false });

  const passwordError = validatePassword(password);
  const confirmError = confirm && password !== confirm ? "validation.password.mismatch" : null;
  const hasError = passwordError || confirmError;
  const missingToken = !token;
  const errorMessage = missingToken
    ? t("login.resetPasswordMissingToken")
    : status.state === "error"
      ? status.message
      : "";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setTouched({ password: true, confirm: true });
    if (!token) {
      setStatus({ state: "error", message: t("login.resetPasswordMissingToken") });
      return;
    }
    if (hasError) {
      setStatus({ state: "error", message: t("validation.fixErrors") });
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setStatus({ state: "success", message: t("login.resetPasswordSuccessBody") });
    } catch (err) {
      const messageKey = err.response?.data?.message_key;
      const message = err.response?.data?.message;
      setStatus({
        state: "error",
        message: t(messageKey || "errors.generic", {
          defaultValue: message || t("errors.generic"),
        }),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-brand">
        <img alt={t("login.logoAlt")} className="login-logo" src={logoSmall} />
        <h1>{t("app.title")}</h1>
        <p className="login-tagline">{t("app.tagline")}</p>
      </div>

      <form className="login-card" onSubmit={handleSubmit}>
        <div className="login-card__header">
          <h2 className="login-title">{t("login.resetPasswordTitle")}</h2>
          <p className="login-subtitle">{t("login.resetPasswordSubtitle")}</p>
        </div>

        {errorMessage && (
          <div className="login-status login-status--error">{errorMessage}</div>
        )}
        {status.state === "success" && (
          <div className="login-status login-status--success">
            <strong>{t("login.resetPasswordSuccessTitle")}</strong>
            <p>{status.message}</p>
          </div>
        )}

        <div className="login-field">
          <label htmlFor="new-password">{t("login.resetPasswordNewLabel")}</label>
          <div className="login-password-input">
            <input
              id="new-password"
              name="new-password"
              type={showPassword ? "text" : "password"}
              placeholder={t("login.resetPasswordNewPlaceholder")}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
              disabled={status.state === "success" || missingToken}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="login-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
            >
              {showPassword ? "🙈" : "👁️"}
            </button>
          </div>
          {touched.password && passwordError ? (
            <p className="login-field__error" role="alert">{t(passwordError)}</p>
          ) : null}
        </div>

        <div className="login-field">
          <label htmlFor="confirm-password">{t("login.resetPasswordConfirmLabel")}</label>
          <div className="login-password-input">
            <input
              id="confirm-password"
              name="confirm-password"
              type={showConfirm ? "text" : "password"}
              placeholder={t("login.resetPasswordConfirmPlaceholder")}
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, confirm: true }))}
              disabled={status.state === "success" || missingToken}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="login-toggle"
              onClick={() => setShowConfirm((prev) => !prev)}
              aria-label={showConfirm ? t("login.hidePassword") : t("login.showPassword")}
            >
              {showConfirm ? "🙈" : "👁️"}
            </button>
          </div>
          {touched.confirm && confirmError ? (
            <p className="login-field__error" role="alert">{t(confirmError)}</p>
          ) : null}
        </div>

        <button
          className="login-submit"
          type="submit"
          disabled={status.state === "success" || submitting || missingToken}
        >
          {submitting ? t("common.saving") : t("login.resetPasswordSubmit")}
        </button>

        <div className="login-links">
          <a href="/login" className="login-link-primary">{t("login.backToLogin")}</a>
          <a href="/forgot-password" className="login-link-secondary">{t("login.forgotPassword")}</a>
        </div>
      </form>
    </div>
  );
}
